/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as ModalActionCreators from '@app/actions/ModalActionCreators';
import {modal} from '@app/actions/ModalActionCreators';
import {ExternalLink} from '@app/components/common/ExternalLink';
import {AgeVerificationModal} from '@app/components/modals/AgeVerificationModal';
import * as Modal from '@app/components/modals/Modal';
import {Button} from '@app/components/uikit/button/Button';
import {HelpCenterArticleSlug} from '@app/constants/HelpCenterConstants';
import {NSFWGateReason} from '@app/stores/GuildNSFWAgreeStore';
import {getRegionDisplayName} from '@app/utils/GeoUtils';
import * as HelpCenterUtils from '@app/utils/HelpCenterUtils';
import {getEffectiveNSFWGeoContext, isAgeVerificationAvailableInRegion} from '@app/utils/NSFWGeoUtils';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useCallback} from 'react';

interface MediaNSFWGateDetailsModalProps {
	reason: NSFWGateReason;
}

export const MediaNSFWGateDetailsModal = observer(({reason}: MediaNSFWGateDetailsModalProps) => {
	const {i18n, t} = useLingui();

	const handleClose = useCallback(() => {
		ModalActionCreators.pop();
	}, []);

	const handleOpenAgeVerification = useCallback(() => {
		ModalActionCreators.pop();
		ModalActionCreators.push(modal(() => <AgeVerificationModal />));
	}, []);

	if (reason === NSFWGateReason.GEO_RESTRICTED) {
		const {countryCode, regionCode} = getEffectiveNSFWGeoContext();
		const regionName = getRegionDisplayName(i18n, countryCode ?? undefined, regionCode ?? undefined);
		const ageVerificationAvailable = isAgeVerificationAvailableInRegion();

		return (
			<Modal.Root size="small" centered>
				<Modal.Header title={t`Age-Restricted Media`} />
				<Modal.Content>
					<Modal.ContentLayout>
						<Modal.Description>
							<Trans>
								Due to age verification laws in {regionName}, this media is not available from your current region.
							</Trans>
						</Modal.Description>
						{ageVerificationAvailable ? (
							<Modal.Description>
								<Trans>Verify your age to unlock age-restricted media.</Trans>
							</Modal.Description>
						) : (
							<Modal.Description>
								<Trans>Age verification is currently available only in the UK.</Trans>
							</Modal.Description>
						)}
					</Modal.ContentLayout>
				</Modal.Content>
				<Modal.Footer>
					{ageVerificationAvailable ? (
						<Button onClick={handleClose} variant="secondary">
							<Trans>Close</Trans>
						</Button>
					) : (
						<Button onClick={handleClose} variant="primary">
							<Trans>Got It</Trans>
						</Button>
					)}
					{ageVerificationAvailable && (
						<Button onClick={handleOpenAgeVerification} variant="primary">
							<Trans>Verify Your Age</Trans>
						</Button>
					)}
				</Modal.Footer>
			</Modal.Root>
		);
	}

	return (
		<Modal.Root size="small" centered>
			<Modal.Header title={t`Age-Restricted Media`} />
			<Modal.Content>
				<Modal.ContentLayout>
					<Modal.Description>
						<Trans>
							You must be 18 or older to view this media.{' '}
							<ExternalLink href={HelpCenterUtils.getURL(HelpCenterArticleSlug.ChangeDateOfBirth)}>
								Learn more
							</ExternalLink>
						</Trans>
					</Modal.Description>
				</Modal.ContentLayout>
			</Modal.Content>
			<Modal.Footer>
				<Button onClick={handleClose} variant="primary">
					<Trans>Close</Trans>
				</Button>
			</Modal.Footer>
		</Modal.Root>
	);
});
